import { Injectable } from '@angular/core';

import { of } from 'rxjs';
import { catchError, filter, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';

import { LoggerService } from '../../../core/services/logger.service';
import { SnackBarService } from '../../../core/services/snackbar.service';
import * as uiActions from '../../../modules/shared/store/ui.actions';
import { ChannelsService } from '../services/channels.service';
import { MessagesService } from '../services/messages.service';
import { MessagingApiService } from '../services/messaging-api/messaging-api.service';
import { NotificationsApiService } from '../services/notifications-api.service';
import { PermissionsService } from '../services/permissions.service';
import { SmartRepliesService } from '../services/smart-replies.service';
import { StandardRepliesService } from '../services/standard-replies.service';
import * as chatActions from './chat.actions';
import { AppStateWithChat } from './chat.reducer';
import { ChatEffectsBase } from './chat-effects-base';

@Injectable()
export class NotificationsEffects extends ChatEffectsBase {
  constructor(
    actions$: Actions,
    store: Store<AppStateWithChat>,
    messagingApiService: MessagingApiService,
    snackBarService: SnackBarService,
    channelsService: ChannelsService,
    messagesService: MessagesService,
    permissionsService: PermissionsService,
    logger: LoggerService,
    private notificationsApiService: NotificationsApiService,
    private smartRepliesService: SmartRepliesService,
    private standardRepliesService: StandardRepliesService
  ) {
    super(
      actions$,
      store,
      messagingApiService,
      snackBarService,
      channelsService,
      messagesService,
      permissionsService,
      logger
    );
  }

  getUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(chatActions.getUsers),
      mergeMap(({ page, size }) => {
        this.store.dispatch(uiActions.setLoadingUsers({ value: true }));
        return this.messagingApiService.searchUsers({ searchTerm: '', page: page ?? 1, size: size ?? 20 }).pipe(
          mergeMap((users) => {
            return [chatActions.setUsers({ users }), uiActions.setLoadingUsers({ value: false })];
          }),
          catchError((error) => {
            this.logger.handleEffectError(error);
            return [uiActions.setLoadingUsers({ value: false })];
          })
        );
      })
    )
  );

  getMessagesTemplates$ = createEffect(() =>
    this.actions$.pipe(
      ofType(chatActions.getMessagesTemplates),
      switchMap(({ channel }) => {
        return this.notificationsApiService.getMessagesTemplates(channel).pipe(
          mergeMap((templates) => [chatActions.setMessagesTemplates({ templates })]),
          catchError((error) => {
            this.logger.handleEffectError(error);
            return of();
          })
        );
      })
    )
  );

  getTags$ = createEffect(() =>
    this.actions$.pipe(
      ofType(chatActions.getTags),
      switchMap(() => {
        return this.messagingApiService.getTags().pipe(
          mergeMap((tags) => {
            return [chatActions.setTags({ tags }), uiActions.setLoadingTags({ value: false })];
          }),
          catchError((err) => {
            this.logger.handleEffectError(err);
            this.snackBarService.showErrorSnackBar('Failed to get teams, please try again');
            return [uiActions.setLoadingTags({ value: false })];
          })
        );
      })
    )
  );

  getSmartReplies$ = createEffect(() =>
    this.actions$.pipe(
      ofType(chatActions.getSmartReplies),
      mergeMap(({ channel }) => {
        return this.smartRepliesService.getSuggestions(channel).pipe(
          mergeMap((smartReplies) => {
            return [
              chatActions.setSmartRepliesStatus({ value: false, channel }),
              chatActions.setSmartReplies({ smartReplies, channelUID: channel.uid }),
              chatActions.setLoadingSmartReplies({ value: false, channelUID: channel.uid }),
            ];
          }),
          catchError((err) => {
            this.logger.handleEffectError(err);
            this.snackBarService.showErrorSnackBar('Failed to get smart replies, please try again');
            return [
              chatActions.setLoadingSmartReplies({ value: false, channelUID: channel.uid }),
              chatActions.setSmartRepliesEnabled({ value: false }),
            ];
          })
        );
      })
    )
  );

  getStandardReplies$ = createEffect(() =>
    this.actions$.pipe(
      ofType(chatActions.getStandardReplies),
      withLatestFrom(
        this.store.select((state) => {
          return state.chat.selectedWorkSpace;
        })
      ),
      filter(([, selectedWorkSpace]) => selectedWorkSpace !== null),
      mergeMap(([action, selectedWorkSpace]) => {
        const workspace = selectedWorkSpace!;
        return this.standardRepliesService.getAutoResponses(action.channel).pipe(
          mergeMap((replies) => {
            return [
              chatActions.setStandardReplies({
                standardReplies: replies,
                channel: action.channel,
                selectedWorkspace: workspace,
                context: this.standardRepliesService.getContext(action.channel),
              }),
              chatActions.setLoadingStandardReplies({ value: false, channelUID: action.channel.uid }),
            ];
          }),
          catchError(() => {
            return [chatActions.setLoadingStandardReplies({ value: false, channelUID: action.channel.uid })];
          })
        );
      })
    )
  );

  getUnreadMessages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(chatActions.getUnreadMessages),
      mergeMap(() => {
        return this.messagingApiService.getUnreads().pipe(
          mergeMap((data) => {
            return [chatActions.setUnreadMessages({ count: data.count })];
          }),
          catchError((err) => {
            this.logger.handleEffectError(err);
            return [];
          })
        );
      })
    )
  );
}
